import { useCallback, useEffect, useId, useRef } from 'react';
import MenuItem from '../Menu/MenuItem.jsx';
import MenuPanel from '../Menu/MenuPanel.jsx';
import {
  FILTERS_SORT_FIELD_LABEL,
  FILTERS_SORT_FIELD_ROW,
  FILTERS_SORT_GROUP,
  FILTERS_SORT_ICON_BUTTON,
  FILTERS_SORT_MENU_ANCHOR,
} from './filtersTokens.js';
import { joinClasses } from './menuFilter.utils.js';
import { ArrowDown, ArrowUp, ChevronDown } from 'lucide-react';

/**
 * Sort controls — Figma 7996:5557 / 7996:5370.
 * Ghost direction icon + label + chevron; options en {@link MenuPanel} + {@link MenuItem}.
 *
 * @param {object} props
 * @param {import('./filtersTypes.js').FilterSortConfig} props.sort — `options`: `{ value, label }[]`
 * @param {boolean} props.isOpen
 * @param {() => void} props.onOpen
 * @param {() => void} props.onClose
 * @param {{ sortAsc: string, sortDesc: string }} props.i18n
 */
const FilterSortControls = ({ sort, isOpen, onOpen, onClose, i18n }) => {
  const generatedId = useId();
  const menuId = `sort-field-${generatedId}`;
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const sortLabel = sort.value
    ? sort.options.find((o) => o.value === sort.value)?.label ?? sort.label ?? 'Name'
    : sort.label ?? 'Name';

  const handleSelect = useCallback(
    (optValue) => {
      sort.onChange?.(optValue);
      onClose?.();
    },
    [sort.onChange, onClose],
  );

  const handleDirectionToggle = useCallback(
    () => sort.onDirectionChange?.(sort.direction === 'asc' ? 'desc' : 'asc'),
    [sort.onDirectionChange, sort.direction],
  );

  const toggleOpen = useCallback(() => {
    if (sort.disabled) return;
    if (isOpen) onClose?.();
    else onOpen?.();
  }, [sort.disabled, isOpen, onClose, onOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onDocMouseDown = (e) => {
      const panel = panelRef.current;
      const trigger = triggerRef.current;
      if (panel?.contains(e.target) || trigger?.contains(e.target)) return;
      onClose?.();
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  return (
    <div className={FILTERS_SORT_GROUP}>
      <button
        type="button"
        disabled={sort.disabled}
        onClick={handleDirectionToggle}
        aria-label={sort.direction === 'asc' ? i18n.sortAsc : i18n.sortDesc}
        aria-pressed={sort.direction === 'asc'}
        className={FILTERS_SORT_ICON_BUTTON}
      >
        {sort.direction === 'asc' ? (
          <ArrowUp size={16} aria-hidden />
        ) : (
          <ArrowDown size={16} aria-hidden />
        )}
      </button>

      <div className={FILTERS_SORT_FIELD_ROW}>
        <button
          ref={triggerRef}
          type="button"
          disabled={sort.disabled}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          aria-controls={menuId}
          onClick={toggleOpen}
          className={joinClasses(
            'inline-flex items-center gap-2 outline-none',
            'focus-visible:rounded focus-visible:ring-1 focus-visible:ring-accent/50',
            sort.disabled && 'cursor-not-allowed opacity-50',
          )}
        >
          <span className={FILTERS_SORT_FIELD_LABEL}>{sortLabel}</span>
          <span
            className="inline-flex size-7 shrink-0 items-center justify-center text-content-secondary"
            aria-hidden
          >
            <ChevronDown
              size={16}
              className={joinClasses('transition-transform', isOpen && 'rotate-180')}
            />
          </span>
        </button>
      </div>

      <div
        ref={panelRef}
        id={menuId}
        aria-hidden={!isOpen || undefined}
        className={joinClasses(
          FILTERS_SORT_MENU_ANCHOR,
          !isOpen && 'invisible pointer-events-none',
        )}
      >
        <MenuPanel role="menu" ariaLabel="Sort by" className="w-[16.25rem]">
          {sort.options.map((opt) => (
            <MenuItem
              key={opt.value}
              size="small"
              label={opt.label}
              selected={sort.value === opt.value}
              disabled={opt.disabled}
              onClick={() => {
                if (!opt.disabled) handleSelect(opt.value);
              }}
            />
          ))}
        </MenuPanel>
      </div>
    </div>
  );
};

export default FilterSortControls;
